/* eslint-disable fp/no-mutation */
import { useFormik } from 'formik'
import * as yup from 'yup'
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Paper,
  TextField,
  Typography,
} from '@mui/material'
import React, { ReactElement, useState, useEffect } from 'react'
import axios from 'src/utilities/http-client'
import { useAppDispatch, useAppSelector } from 'src/store/hooks'
import { fetchSections, selectSectionByID } from 'src/v3/create/create.slice'
import ImageUpload from 'src/v3/components/upload-image'
import { uploadImageToS3 } from 'src/v3/utilities/s3'

interface CategoryModalProps {
  open: boolean
  onClose: () => void
  onSuccess: (sectionId: string) => void
  editingSectionId?: string
  linkedServiceId: string
}

export default ({
  open,
  onClose,
  onSuccess,
  editingSectionId,
  linkedServiceId,
}: CategoryModalProps): ReactElement => {
  let dispatch = useAppDispatch()
  let isEditMode = !!editingSectionId
  let existingSection = useAppSelector(
    editingSectionId ? selectSectionByID(editingSectionId) : () => undefined,
  )

  let [categoryThumbnail, setCategoryThumbnail] = useState<File | undefined>(
    undefined,
  )
  let [savingCategory, setSavingCategory] = useState<boolean>(false)
  let [categoryError, setCategoryError] = useState<string>('')

  let categoryForm = useFormik({
    enableReinitialize: true,
    initialValues: {
      name: existingSection?.name || '',
      description: existingSection?.description || '',
      pageRank: existingSection?.pageRank || 0,
      linkedServiceId: linkedServiceId || '',
    },
    validationSchema: yup.object({
      name: yup.string().required('Category name is required'),
      description: yup.string().required('Description is required'),
      pageRank: yup
        .number()
        .min(0, 'Page rank must be 0 or greater')
        .required('Page rank is required'),
    }),
    onSubmit: async (values) => {
      setSavingCategory(true)
      setCategoryError('')
      try {
        let response

        if (isEditMode && editingSectionId) {
          // Update existing section
          response = await axios.put('/api/v2/create/section', {
            id: editingSectionId,
            name: values.name,
            description: values.description,
            libraryId: '0f6846fb-027d-4bde-899a-e8beb373e668',
            linkedServiceId:
              values.linkedServiceId.toLowerCase() !== 'foamfrat'
                ? values.linkedServiceId
                : null,
            pageRank: values.pageRank,
          })
        } else {
          // Create new section
          response = await axios.post('/api/v2/create/section', {
            name: values.name,
            description: values.description,
            libraryId: '0f6846fb-027d-4bde-899a-e8beb373e668',
            linkedServiceId:
              values.linkedServiceId.toLowerCase() !== 'foamfrat'
                ? values.linkedServiceId
                : null,
            pageRank: values.pageRank,
          })
        }

        let section = response.data

        // Upload thumbnail if provided
        if (categoryThumbnail && section?.id) {
          // Convert File to base64 string
          let categoryThumbnailBase64 = await new Promise<string>((resolve) => {
            let reader = new FileReader()
            reader.onloadend = () => resolve(reader.result as string)
            reader.readAsDataURL(categoryThumbnail)
          })

          // Get signed URL and upload to S3
          await axios
            .put('/api/v2/create/section/image', { id: section.id })
            .then((uploadResponse) =>
              uploadImageToS3(categoryThumbnailBase64, uploadResponse.data),
            )
        }

        // Refresh sections
        // eslint-disable-next-line no-restricted-syntax -- TODO(ENG-694): pre-existing await dispatch/.unwrap() pattern; refactor to selector-driven state
        await dispatch(fetchSections())

        // Call success callback with section ID
        onSuccess(section.id)

        // Reset form and close modal
        categoryForm.resetForm()
        setCategoryThumbnail(undefined)
      } catch (err: any) {
        setCategoryError(
          err?.response?.data ||
            err?.message ||
            `Error ${isEditMode ? 'updating' : 'creating'} category`,
        )
      } finally {
        setSavingCategory(false)
      }
    },
  })

  // Reset form when modal opens/closes or editingSectionId changes
  useEffect(() => {
    if (!open) {
      categoryForm.resetForm()
      setCategoryThumbnail(undefined)
      setCategoryError('')
    }
  }, [open, editingSectionId])

  return (
    <Dialog
      open={open}
      onClose={() => !savingCategory && onClose()}
      fullWidth
      maxWidth="sm"
    >
      <Paper sx={{ px: 4, py: 2 }}>
        <form onSubmit={categoryForm.handleSubmit}>
          <DialogTitle sx={{ textAlign: 'center' }}>
            {isEditMode
              ? `Edit Category: ${existingSection?.name || ''}`
              : 'Create New Category'}
          </DialogTitle>
          <DialogContent sx={{ px: 2, pb: 6 }}>
            <Box
              sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 1 }}
            >
              <TextField
                fullWidth
                label="Category Name"
                name="name"
                value={categoryForm.values.name}
                onChange={categoryForm.handleChange}
                error={categoryForm.touched.name && !!categoryForm.errors.name}
                helperText={
                  categoryForm.touched.name && categoryForm.errors.name
                }
                disabled={savingCategory}
                slotProps={{
                  input: {
                    sx: { bgcolor: 'background.paper' },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Description"
                name="description"
                multiline
                rows={3}
                value={categoryForm.values.description}
                onChange={categoryForm.handleChange}
                error={
                  categoryForm.touched.description &&
                  !!categoryForm.errors.description
                }
                helperText={
                  categoryForm.touched.description &&
                  categoryForm.errors.description
                }
                disabled={savingCategory}
                slotProps={{
                  input: {
                    sx: { bgcolor: 'background.paper' },
                  },
                }}
              />

              <TextField
                fullWidth
                label="Page Rank"
                name="pageRank"
                type="number"
                value={categoryForm.values.pageRank}
                onChange={categoryForm.handleChange}
                error={
                  categoryForm.touched.pageRank &&
                  !!categoryForm.errors.pageRank
                }
                helperText={
                  (categoryForm.touched.pageRank &&
                    categoryForm.errors.pageRank) ||
                  'Lower numbers appear first'
                }
                disabled={savingCategory}
                slotProps={{
                  input: {
                    sx: { bgcolor: 'background.paper' },
                  },

                  htmlInput: { min: 0 },
                }}
              />

              <Box>
                <Typography variant="subtitle2" sx={{ mb: 2 }}>
                  Category Thumbnail (Optional)
                </Typography>
                <ImageUpload
                  id="category-thumbnail"
                  type="thumbnail"
                  image={categoryThumbnail}
                  setImage={setCategoryThumbnail}
                  existingImage={existingSection?.imageName}
                  imageBasePath="public"
                />
              </Box>

              {categoryError && <Alert severity="error">{categoryError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions
            disableSpacing={true}
            sx={{ flexDirection: 'column', gap: 2, pt: 0, pb: 6, mt: 0 }}
          >
            <Button
              type="submit"
              color="primary"
              variant="contained"
              fullWidth
              disabled={savingCategory}
            >
              {savingCategory
                ? isEditMode
                  ? 'Saving...'
                  : 'Creating...'
                : isEditMode
                  ? 'Save Changes'
                  : 'Create Category'}
            </Button>
            <Button
              onClick={onClose}
              color="error"
              variant="contained"
              fullWidth
              disabled={savingCategory}
            >
              Cancel
            </Button>
          </DialogActions>
        </form>
      </Paper>
    </Dialog>
  )
}
